/**
 * 入力項目 (userFields / postFields) の選択肢「その他」に付く記入欄 (2026-10-06)。
 *
 * 管理者が項目の設定で「その他」を追加し (`FieldDef.otherOption` = その選択肢の value、選択肢の
 * 最後に入る)、自由記述欄を付ける設定 (`otherTextInput`、既定 = 付ける) にすると、利用者の入力
 * フォームでその選択肢の後ろに自由記述欄が出る。自動検出はしない (選択肢に「その他」と書いた
 * だけでは記述欄は出ない)。
 *
 * 保存の約束:
 *   - 選んだ値はこれまでどおり選択肢の value のまま (例: 'その他' / ['A','その他'])。
 *     分析ツールの集計・絞り込み・色分けは値の形が変わらないのでそのまま動く。
 *   - 記入内容は同じ辞書の別キー `{key}__other` に文字列で入れる
 *     (投稿 = post.extraFields、参加者 = membership.profileFieldValues。組込項目の性別等も
 *     profileFieldValues 側に入れる)。
 *   - 「その他」を外したら記入内容も消す (`applyChoiceChange`)。
 *
 * 表示・出力は `formatChoiceWithOther` で「その他（記入内容）」の形に揃える。
 */
/** 記入内容を入れる別キーの接尾辞。 */
export const OTHER_TEXT_KEY_SUFFIX = '__other';
/** 記入欄の最大文字数。 */
export const OTHER_TEXT_MAX_LENGTH = 100;
/** 項目 key → 記入内容を入れる key。 */
export function otherTextKey(fieldKey) {
    return `${fieldKey}${OTHER_TEXT_KEY_SUFFIX}`;
}
/** 記入内容用の key か (項目一覧に無い key を列挙する処理で除外するため)。 */
export function isOtherTextKey(key) {
    return key.endsWith(OTHER_TEXT_KEY_SUFFIX) && key.length > OTHER_TEXT_KEY_SUFFIX.length;
}
/** 管理画面で追加する「その他」の選択肢の文言 (= 保存値)。 */
export const OTHER_OPTION_LABEL = 'その他';
/**
 * 管理者が追加した「その他」の選択肢を返す (自由記述の有無は問わない、管理画面用)。
 * 追加していない / 選択式でない / options に見当たらない (選択肢を手で消した等) → null。
 */
export function findAddedOtherOption(field) {
    if (field.type !== 'select' && field.type !== 'multiselect')
        return null;
    if (typeof field.otherOption !== 'string' || field.otherOption === '')
        return null;
    const options = Array.isArray(field.options) ? field.options : [];
    return options.find((o) => o && o.value === field.otherOption) ?? null;
}
/**
 * 自由記述欄を出す「その他」の選択肢を返す。「その他」が無い / 記述欄を付けない設定 → null。
 * 入力フォーム・表示・出力はこちらを使う。
 */
export function findOtherOption(field) {
    if (field.otherTextInput === false)
        return null;
    return findAddedOtherOption(field);
}
/**
 * 管理画面の「その他を追加」の切替。options の最後に「その他」を入れる / 取り除く差分を返す。
 * 同じ文言の選択肢が既にあれば重ねず、最後へ移す。
 */
export function toggleOtherOption(field, on) {
    const current = field.otherOption || OTHER_OPTION_LABEL;
    const rest = field.options.filter((o) => o.value !== current && o.value !== OTHER_OPTION_LABEL);
    // 外すときは '' を残す (未設定にするとテンプレ側の既定値が merge で戻りうるため)。
    if (!on)
        return { options: rest, otherOption: '' };
    return {
        options: [...rest, { value: OTHER_OPTION_LABEL, label: OTHER_OPTION_LABEL }],
        otherOption: OTHER_OPTION_LABEL,
        // 追加した時点では自由記述欄を付ける (管理者が外せる)。
        otherTextInput: true,
    };
}
/** 管理画面の選択肢テキスト欄用: 「その他」を除いた選択肢 (「その他」はチェックで管理する)。 */
export function optionsWithoutOther(field) {
    return field.otherOption
        ? field.options.filter((o) => o.value !== field.otherOption)
        : field.options;
}
/** 管理画面の選択肢テキスト欄を編集したとき: 「その他」を常に最後に付け直す。 */
export function withOtherOptionLast(field, edited) {
    const other = field.otherOption;
    if (!other)
        return edited;
    return [...edited.filter((o) => o.value !== other), { value: other, label: other }];
}
/** 選択値 (string / string[]) に「その他」が含まれるか。 */
export function isOtherChosen(otherValue, value) {
    if (Array.isArray(value))
        return value.includes(otherValue);
    return value === otherValue;
}
/** 辞書から記入内容を読む (前後空白は除く、無ければ '')。 */
export function readOtherText(values, fieldKey) {
    const v = values?.[otherTextKey(fieldKey)];
    return typeof v === 'string' ? v.trim() : '';
}
/**
 * 選択値を変えたときの辞書を返す。「その他」が外れたら記入内容のキーを消す。
 * 「その他」の無い項目では `{...values, [key]: next}` と同じ。
 */
export function applyChoiceChange(values, field, next) {
    const out = { ...values, [field.key]: next };
    const other = findOtherOption(field);
    if (other && !isOtherChosen(other.value, next))
        delete out[otherTextKey(field.key)];
    return out;
}
/** 表示用: 「その他」の選択肢ラベルに記入内容を添える (記入が無ければラベルのまま)。 */
export function formatChoiceWithOther(label, otherText) {
    const t = (otherText ?? '').trim();
    return t ? `${label}（${t}）` : label;
}
/**
 * 選択値を表示文字列の配列にする (value → label、「その他」には記入内容を添える)。
 * 単一選択は要素 1 つ、未選択は空配列。
 */
export function choiceDisplayLabels(field, value, values) {
    const raw = Array.isArray(value)
        ? value
        : value === undefined || value === null || value === ''
            ? []
            : [value];
    const other = findOtherOption(field);
    const otherText = readOtherText(values, field.key);
    const options = Array.isArray(field.options) ? field.options : [];
    return raw.map((v) => {
        const s = String(v);
        const label = options.find((o) => o.value === s)?.label ?? s;
        return other && s === other.value ? formatChoiceWithOther(label, otherText) : label;
    });
}
/**
 * 必須の項目で「その他」を選んだのに記入が空か (送信前の検証用)。
 * 必須でない項目は記入が空でもよい。
 */
export function isOtherTextMissing(field, values, value) {
    if (field.mode !== 'required')
        return false;
    const other = findOtherOption(field);
    if (!other || !isOtherChosen(other.value, value))
        return false;
    return readOtherText(values, field.key) === '';
}
//# sourceMappingURL=fieldOtherText.js.map